<template>
  <header class="header-v2">
    <div class="header-inner">
      <div class="logo" @click="goHome">
        <img :src="logoSrc" :alt="siteConfig.siteName || '山东盛博莱电力工程有限公司'" />
        <div class="logo-text">
          <strong>{{ displaySiteName }}</strong>
          <span>{{ $t('header.englishName') }}</span>
        </div>
      </div>
      <nav class="main-nav">
        <div v-for="item in menuList" :key="item.path" class="nav-item" :class="{ 'has-sub': item.children, 'active': isActive(item.path) }">
          <router-link v-if="!item.children" :to="item.path" :class="{ active: isActive(item.path) }">
            {{ $t(item.nameKey) }}
          </router-link>
          <div v-else class="nav-item-wrap">
            <router-link :to="item.path" :class="{ active: isActive(item.path) }">{{ $t(item.nameKey) }}</router-link>
            <div class="submenu-v2">
              <router-link v-for="sub in item.children" :key="sub.path" :to="sub.path">
                {{ $t(sub.nameKey) }}
              </router-link>
            </div>
          </div>
        </div>
      </nav>
      <button class="lang-toggle" type="button" @click="toggleLang">{{ $t('header.langToggle') }}</button>
      <div class="mobile-toggle" @click="menuOpen = !menuOpen">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
    <div class="mobile-nav" :class="{ open: menuOpen }">
      <div v-for="item in menuList" :key="item.path" class="mobile-nav-item" :class="{ 'has-sub': item.children, 'open': item.open }">
        <div class="mobile-nav-title" @click="toggleMobileSub(item)">
          <router-link :to="item.path" @click.native="!item.children && (menuOpen = false)">{{ $t(item.nameKey) }}</router-link>
          <i v-if="item.children" class="arrow"></i>
        </div>
        <div v-if="item.children" class="mobile-submenu-v2">
          <router-link v-for="sub in item.children" :key="sub.path" :to="sub.path" @click.native="menuOpen = false">
            {{ $t(sub.nameKey) }}
          </router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { getSiteConfig } from '@/api/portal'
import { resolveFileUrl } from '@/utils/request'
import { toggleLocale } from '@/utils/lang'
import defaultLogo from '@/assets/images/senbole-logo-mark.png'

export default {
  name: 'HeaderV2',
  data() {
    return {
      menuOpen: false,
      siteConfig: {},
      menuList: [
        { nameKey: 'common.home', path: '/' },
        {
          nameKey: 'common.about', path: '/about',
          children: [
            { nameKey: 'common.profile', path: '/about/profile' },
            { nameKey: 'common.structure', path: '/about/structure' },
            { nameKey: 'common.culture', path: '/about/culture' },
            { nameKey: 'common.honors', path: '/about/honors' }
          ]
        },
        {
          nameKey: 'common.news', path: '/news',
          children: [
            { nameKey: 'common.companyNews', path: '/news/list' },
            { nameKey: 'common.journal', path: '/news/internal' },
            { nameKey: 'common.safety', path: '/news/safety' }
          ]
        },
        {
          nameKey: 'common.cases', path: '/cases',
          children: [
            { nameKey: 'common.debugCases', path: '/cases/debug' },
            { nameKey: 'common.operationCases', path: '/cases/operation' }
          ]
        },
        {
          nameKey: 'common.enterprise', path: '/enterprise',
          children: [
            { nameKey: 'common.stars', path: '/enterprise/stars' },
            { nameKey: 'common.staff', path: '/enterprise/staff' }
          ]
        },
        { nameKey: 'common.contact', path: '/contact' }
      ]
    }
  },
  computed: {
    logoSrc() {
      return resolveFileUrl(this.siteConfig.logoUrl, defaultLogo)
    },
    displaySiteName() {
      return this.$langState.locale === 'en'
        ? 'Shandong Senbole Power'
        : (this.siteConfig.siteName || '山东盛博莱电力工程有限公司')
    }
  },
  created() {
    this.loadSiteConfig()
  },
  methods: {
    loadSiteConfig() {
      getSiteConfig().then(data => {
        this.siteConfig = data || {}
      }).catch(() => {})
    },
    isActive(path) {
      if (path === '/') {
        return this.$route.path === '/'
      }
      return this.$route.path.startsWith(path)
    },
    goHome() {
      this.$router.push('/')
    },
    toggleLang() {
      toggleLocale()
    },
    toggleMobileSub(item) {
      if (item.children) {
        item.open = !item.open
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.header-v2 {
  background: #0f172a;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}

.header-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  flex-shrink: 0;

  img {
    width: 42px;
    height: 42px;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 6px 18px rgba(239, 0, 18, 0.26);
  }
}

.logo-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;

  strong {
    max-width: 230px;
    overflow: hidden;
    color: #fff;
    font-size: 16px;
    line-height: 1.25;
    font-weight: 700;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  span {
    margin-top: 2px;
    color: rgba(255,255,255,0.48);
    font-size: 11px;
    line-height: 1.2;
    font-weight: 700;
  }
}

.main-nav {
  display: flex;
  gap: 8px;

  .nav-item {
    position: relative;

    > a, .nav-item-wrap > a {
      display: block;
      color: rgba(255,255,255,0.7);
      font-size: 15px;
      padding: 8px 20px;
      border-radius: 8px;
      transition: all 0.3s;

      &:hover,
      &.active {
        color: #fff;
        background: rgba(59, 130, 246, 0.2);
      }

      &.active {
        background: linear-gradient(135deg, #3b82f6, #06b6d4);
      }
    }

    &:hover .submenu-v2 {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }
}

.lang-toggle {
  flex-shrink: 0;
  margin-left: 12px;
  padding: 7px 13px;
  border: 1px solid rgba(255,255,255,0.24);
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.86);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #fff;
    border-color: rgba(255,255,255,0.45);
    background: rgba(239, 0, 18, 0.28);
  }
}

.submenu-v2 {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 180px;
  background: #1e293b;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.4);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s;

  a {
    display: block;
    padding: 10px 16px;
    font-size: 14px;
    color: rgba(255,255,255,0.7);
    border-radius: 8px;
    transition: all 0.3s;
    white-space: nowrap;

    &:hover {
      color: #fff;
      background: rgba(59, 130, 246, 0.2);
    }
  }
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  padding: 8px;

  span {
    display: block;
    width: 24px;
    height: 2px;
    background: #fff;
    transition: all 0.3s;
  }
}

.mobile-nav {
  display: none;
  flex-direction: column;
  background: #0f172a;
  padding: 0 20px;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s;

  &.open {
    max-height: 600px;
    padding-bottom: 20px;
  }
}

.mobile-nav-item {
  border-bottom: 1px solid rgba(255,255,255,0.1);

  .mobile-nav-title {
    display: flex;
    justify-content: space-between;
    align-items: center;

    a {
      display: block;
      flex: 1;
      padding: 14px 0;
      color: rgba(255,255,255,0.7);
      font-size: 15px;

      &:hover {
        color: #3b82f6;
      }
    }

    .arrow {
      display: block;
      width: 20px;
      height: 20px;
      text-align: center;
      line-height: 20px;
      transition: transform 0.3s;

      &::before {
        content: '+';
        font-size: 18px;
        color: rgba(255,255,255,0.5);
      }
    }
  }

  &.open .arrow {
    transform: rotate(45deg);
  }
}

.mobile-submenu-v2 {
  padding-left: 16px;

  a {
    display: block;
    padding: 12px 0;
    font-size: 14px;
    color: rgba(255,255,255,0.5);
    border-top: 1px solid rgba(255,255,255,0.05);

    &:hover {
      color: #3b82f6;
    }
  }
}

@media (max-width: 960px) {
  .header-inner {
    padding: 0 18px;
  }

  .logo img {
    width: 38px;
    height: 38px;
  }

  .logo-text strong {
    max-width: 190px;
    font-size: 14px;
  }

  .logo-text span {
    font-size: 10px;
  }

  .main-nav {
    display: none;
  }
  .lang-toggle {
    margin-left: auto;
  }
  .mobile-toggle {
    display: flex;
  }
  .mobile-nav {
    display: flex;
  }
}
</style>
